'use client';

import { useEffect, useState } from 'react';
import { effectiveTheme, setTheme, type Theme } from '@/lib/theme';

/**
 * Sun/moon button that flips between light and dark. Defaults to whatever the
 * OS prefers; the first click stores an explicit choice. The icon is only shown
 * after mount so server and client markup match (the real theme is applied
 * pre-paint by the inline snippet in the root layout).
 */
export function ThemeToggle() {
  const [theme, setThemeState] = useState<Theme>('light');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setThemeState(effectiveTheme());
    setMounted(true);
  }, []);

  function toggle() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setThemeState(next);
  }

  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      className="vn-theme-toggle"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      aria-pressed={mounted ? isDark : undefined}
    >
      <span className="vn-theme-toggle__icon" aria-hidden="true" suppressHydrationWarning>
        {mounted ? (isDark ? <SunIcon /> : <MoonIcon />) : null}
      </span>
    </button>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}
